import React, { useCallback, useState, useMemo } from 'react';
import ExportConfirmationModal from '../components/ExportConfirmationModal';
// @ts-ignore — JS module without types
import { useProject } from '../config/ProjectContext';

/**
 * Wraps any existing CSV export function with the pre-flight estimator
 * confirmation modal (feature 017). Each call site changes minimally:
 *
 *   const { requestExport, modal } = useGuardedExport({
 *     action: 'brandConsumptions',
 *     projectId, dateIni, dateEnd, filters, title: 'Export consumptions CSV',
 *   }, exportToCSV);
 *
 *   <button onClick={requestExport}>Export</button>
 *   {modal}
 *
 * The modal opens immediately and triggers the upstream estimate probe.
 * Confirming runs the original export function. If the estimate exceeds the
 * 12-minute server-side limit (FR-008), the confirm button is disabled.
 */

export type GuardedExportAction =
  | 'brandConsumptions'
  | 'machineTelemetryConsumption'
  | 'telemetryObject'
  | 'lifeCycleObject'
  | 'sandboxObject';

export interface UseGuardedExportArgs {
  action: GuardedExportAction;
  /** Falls back to the ProjectContext when omitted. */
  projectId?: number;
  dateIni: string;
  dateEnd: string;
  filters?: string;
  serialNumber?: string;
  object?: string;
  queryParams?: string;
  /** Row count from the parent table. When provided, the estimate uses
   *  the rows-based formula (much more accurate than date-range only). */
  totalRows?: number | null;
  title?: string;
  confirmText?: string;
}

export interface UseGuardedExportResult {
  requestExport: () => void;
  modal: React.ReactNode;
  isOpen: boolean;
  close: () => void;
}

const DEFAULT_TITLES: Record<GuardedExportAction, string> = {
  brandConsumptions: 'Export brand consumptions to CSV',
  machineTelemetryConsumption: 'Export machine telemetry to CSV',
  telemetryObject: 'Export telemetry to CSV',
  lifeCycleObject: 'Export lifecycle data to CSV',
  sandboxObject: 'Export raw device data to CSV',
};

export default function useGuardedExport(
  args: UseGuardedExportArgs,
  onConfirm: () => void | Promise<void>,
): UseGuardedExportResult {
  const [isOpen, setOpen] = useState(false);
  const { project: ctxProject } = (useProject?.() || {}) as { project?: { code?: number } };
  const projectId = args.projectId ?? ctxProject?.code;

  const requestExport = useCallback(() => setOpen(true), []);
  const close = useCallback(() => setOpen(false), []);

  const handleConfirm = useCallback(() => {
    Promise.resolve(onConfirm()).catch(() => { /* swallow — caller logs */ });
  }, [onConfirm]);

  const modal = useMemo(() => (
    <ExportConfirmationModal
      isOpen={isOpen}
      onClose={close}
      onConfirm={handleConfirm}
      title={args.title || DEFAULT_TITLES[args.action]}
      action={args.action}
      projectId={projectId as number}
      dateIni={args.dateIni}
      dateEnd={args.dateEnd}
      filters={args.filters}
      serialNumber={args.serialNumber}
      object={args.object}
      queryParams={args.queryParams}
      totalRows={args.totalRows}
      confirmText={args.confirmText || 'Export CSV'}
    />
  ), [
    isOpen, close, handleConfirm,
    args.title, args.action, projectId, args.dateIni, args.dateEnd,
    args.filters, args.serialNumber, args.object, args.queryParams, args.totalRows, args.confirmText,
  ]);

  return { requestExport, modal, isOpen, close };
}
